// Privacy-friendly analytics (F10). Cookie-free; no identifiers. Events go to pluggable sinks.
export type EventName =
  | 'card_open' | 'search' | 'search_no_result' | 'share' | 'layer_change' | 'isolate' | 'landing'
  | 'tour_start' | 'tour_stop' | 'tour_complete' | 'export' | 'offline_download' | 'supporter_view' | 'unlock'
  | 'classroom_mode' | 'engaged_session' | 'body_change';
export const EVENT_NAMES: EventName[] = [
  'card_open', 'search', 'search_no_result', 'share', 'layer_change', 'isolate', 'landing',
  'tour_start', 'tour_stop', 'tour_complete', 'export', 'offline_download', 'supporter_view', 'unlock',
  'classroom_mode', 'engaged_session', 'body_change',
];
export type Props = Record<string, string | number | boolean>;
export type Sink = (name: EventName, props: Props) => void;

const sinks: Sink[] = [];
export const memory: Array<{ name: EventName; props: Props }> = [];

// The engaged-session counter lives in sessionStorage so a reload in the same visit doesn't start over.
// It's a number that disappears with the tab, not an identifier.
const COUNT_KEY = 'marrow.visit';
let fallback = { opens: 0, engaged: false };
function visit(): { opens: number; engaged: boolean } {
  try {
    const raw = sessionStorage.getItem(COUNT_KEY);
    return raw ? (JSON.parse(raw) as { opens: number; engaged: boolean }) : { opens: 0, engaged: false };
  } catch {
    return fallback;
  }
}
function saveVisit(v: { opens: number; engaged: boolean }) {
  fallback = v;
  try {
    sessionStorage.setItem(COUNT_KEY, JSON.stringify(v));
  } catch {
    /* storage blocked: count for this page only */
  }
}

export function addSink(s: Sink) {
  sinks.push(s);
}

export function track(name: EventName, props: Props = {}) {
  memory.push({ name, props });
  if (memory.length > 500) memory.shift();
  for (const s of sinks) {
    try {
      s(name, props);
    } catch {
      /* never let analytics break the app */
    }
  }
  if (name === 'card_open' || name === 'tour_stop') {
    const v = visit();
    if (v.engaged) return;
    v.opens++;
    v.engaged = v.opens >= 3;
    saveVisit(v);
    if (v.engaged) track('engaged_session', {});
  }
}

/** How the visit started, from the first URL (F10 `landing`). */
export function entryOf(pathname: string, search: string): 'home' | 'share-link' | 'structure-page' | 'tour-page' | 'other' {
  if (pathname === '/' || pathname === '/index.html') return 'home';
  if (pathname.startsWith('/t/')) return 'tour-page';
  if (pathname.startsWith('/s/')) return new URLSearchParams(search).has('v') ? 'share-link' : 'structure-page';
  return 'other';
}

/** Search result counts are bucketed so they describe quality, not exact queries. */
export function resultBucket(n: number): string {
  return n === 0 ? '0' : n === 1 ? '1' : n <= 4 ? '2-4' : '5+';
}

/**
 * Starts the configured provider, off the critical path: Umami when VITE_UMAMI_WEBSITE_ID is set, otherwise Plausible
 * when VITE_ANALYTICS_DOMAIN is set, otherwise nothing. Each provider is its own lazily loaded chunk.
 */
export function startAnalytics(env: Record<string, string | undefined> = import.meta.env as Record<string, string | undefined>): Promise<boolean> {
  const endpoint = env.VITE_ANALYTICS_ENDPOINT;
  if (env.VITE_UMAMI_WEBSITE_ID) {
    const websiteId = env.VITE_UMAMI_WEBSITE_ID;
    return import('./umami').then((m) => (addSink(m.umamiSink({ websiteId, endpoint })), true));
  }
  if (env.VITE_ANALYTICS_DOMAIN) {
    const domain = env.VITE_ANALYTICS_DOMAIN;
    return import('./plausible').then((m) => (addSink(m.plausibleSink({ domain, endpoint })), true));
  }
  return Promise.resolve(false);
}

export function resetForTests() {
  memory.length = 0;
  sinks.length = 0;
  fallback = { opens: 0, engaged: false };
  try {
    sessionStorage.removeItem(COUNT_KEY);
  } catch {
    /* ignore */
  }
}
